import { ColDef, ICellRendererParams } from 'ag-grid-community';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { crudStatusEnum, getCrudStatusLabel, permissionLevelEnum, systemConfigKeyEnum } from '../../../../constants';
import { getCrudStatusColor } from '../../../../constants/colors';
import { useUpdateWorkTravelZoneMutation } from '../../../../hooks/mutations/useWorkTravelZoneMutation';
import { useWorkTravelZonesQuery } from '../../../../hooks/queries/useWorkTravelZonesQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';
import { WorkTravelZone } from '../../../../services/apiSLTP/sites/workTravelZones';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import { createDateTimeColumn } from '../../../../utils/tables/commonColumns';
import StateCell from '../../../cells/StateCell';
import UpdateWorkTravelZoneModal from '../../../modals/admin/configuration/UpdateWorkTravelZoneModal';
import DeleteModal from '../../../modals/DeleteModal';
import IconButton from '../../../ui/IconButton';
import IconLine from '../../../ui/IconLine';
import GenericAgGridTable from '../../GenericAgGridTable';

const WorkTravelZonesSettingsRow: React.FC = () => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { getConfigValue } = useSystemConfig();

    const [selectedZone, setSelectedZone] = useState<WorkTravelZone | null>(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);

    const { data: dataZones, isLoading, refetch } = useWorkTravelZonesQuery({});

    const { mutate } = useUpdateWorkTravelZoneMutation({
        onSuccess: () => refetch(),
    });

    const handleEdit = (zone: WorkTravelZone) => {
        setSelectedZone(zone);
        setIsEditMode(true);
        setShowEditModal(true);
    };

    const handleDelete = (zone: WorkTravelZone) => {
        setSelectedZone(zone);
        setShowDeleteModal(true);
    };

    const columnDefs: ColDef<WorkTravelZone>[] = [
        {
            headerName: capitalizeFirstLetter(t('name')),
            field: 'name',
            initialFlex: 1,
            minWidth: 300,
            colId: 'name',
        },
        {
            headerName: capitalizeFirstLetter(t('actions')),
            cellRenderer: (params: ICellRendererParams<WorkTravelZone>) => {
                if (!params.data) return null;
                return (
                    <IconLine>
                        <IconButton
                            iconName="edit"
                            title={capitalizeFirstLetter(t('modifyWorkTravelZone'))}
                            onClick={() => handleEdit(params.data!)}
                            disabled={params.data.crudStatus === crudStatusEnum.DELETED}
                        />
                        {user && user.profileId.permsLevel <= permissionLevelEnum.ADMIN && (
                            <IconButton
                                iconName="delete"
                                title={capitalizeFirstLetter(t('deleteWorkTravelZone'))}
                                onClick={() => handleDelete(params.data!)}
                                disabled={params.data.crudStatus === crudStatusEnum.DELETED}
                            />
                        )}
                    </IconLine>
                );
            },
            colId: 'actions',
            filter: false,
            sortable: false,
        },
        {
            headerName: capitalizeFirstLetter(t('status')),
            valueGetter: (params) => capitalizeFirstLetter(t(getCrudStatusLabel(params.data?.crudStatus))),
            cellRenderer: (params: ICellRendererParams<WorkTravelZone>) => {
                if (!params.data) return params.value;
                return <StateCell stateColor={getCrudStatusColor(params.data?.crudStatus)} text={t(getCrudStatusLabel(params.data?.crudStatus))} />;
            },
            colId: 'status',
        },

        {
            headerName: capitalizeFirstLetter(t('shortName')),
            field: 'shortName',
            colId: 'shortName',
            cellRenderer: (params: ICellRendererParams<WorkTravelZone>) => {
                return (
                    <div
                        style={{
                            display: 'flex',
                            width: 25,
                            height: 25,
                            borderRadius: '50%',
                            backgroundColor: params.data && params.data?.travelPrice > 0 ? 'gold' : 'white',
                            flexShrink: 0,
                            color: '#000',
                            justifyContent: 'center',
                            alignItems: 'center',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                        }}
                    >
                        {params.value}
                    </div>
                );
            },
        },
        {
            headerName: capitalizeFirstLetter(t('minDistance')) + ` (${getConfigValue(systemConfigKeyEnum.DISTANCE_UNIT)})`,
            field: 'minDistance',
            colId: 'minDistance',
            filter: 'agNumberColumnFilter',
            type: 'numericColumn',
        },
        {
            headerName: capitalizeFirstLetter(t('maxDistance')) + ` (${getConfigValue(systemConfigKeyEnum.DISTANCE_UNIT)})`,
            field: 'maxDistance',
            colId: 'maxDistance',
            filter: 'agNumberColumnFilter',
            type: 'numericColumn',
        },
        {
            headerName: capitalizeFirstLetter(t('price')) + ` (${getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)})`,
            field: 'travelPrice',
            colId: 'travelPrice',
            filter: 'agNumberColumnFilter',
            type: 'numericColumn',
        },
        {
            headerName: capitalizeFirstLetter(t('payrollCode')),
            field: 'payrollCode',
            colId: 'payrollCode',
        },
        createDateTimeColumn<WorkTravelZone>('createdAt', 'createdAt'),
        createDateTimeColumn<WorkTravelZone>('updatedAt', 'updatedAt'),
    ];

    if (user?.profileId.permsLevel === permissionLevelEnum.ADMIN) {
        columnDefs.push({
            headerName: capitalizeFirstLetter(t('_id')),
            field: '_id',
            initialWidth: 250,
            cellClass: 'usage-col-main',
            headerClass: 'usage-col-main',
            colId: '_id',
        });
    }

    return (
        <>
            <GenericAgGridTable
                columnDefs={columnDefs}
                rowData={dataZones?.data.sort((a, b) => a.name.localeCompare(b.name)) || []}
                loading={isLoading}
                headerRightComponent={
                    <IconButton
                        iconName="addCircle"
                        onClick={() => {
                            setSelectedZone(null);
                            setIsEditMode(false);
                            setShowEditModal(true);
                        }}
                        title={capitalizeFirstLetter(t('addWorkTravelZone'))}
                    />
                }
                exportCsvFileName="admin-work-travel-zones"
                agGridProps={{ getRowId: ({ data }) => data._id }}
            />

            {showEditModal && (
                <UpdateWorkTravelZoneModal
                    showEditModal={showEditModal}
                    setShowEditModal={setShowEditModal}
                    workTravelZone={selectedZone}
                    isEditMode={isEditMode}
                    refetch={refetch}
                />
            )}

            {showDeleteModal && selectedZone && (
                <DeleteModal
                    open={showDeleteModal}
                    handleClose={() => setShowDeleteModal(false)}
                    deleteAction={() => mutate({ id: selectedZone._id, data: { crudStatus: crudStatusEnum.DELETED } })}
                    title={t('workTravelZone')}
                    itemName={selectedZone.name}
                />
            )}
        </>
    );
};

export default WorkTravelZonesSettingsRow;
